import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, XCircle } from "@phosphor-icons/react";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [virtualJobs, setVirtualJobs] = useState([]);
  const [virtualApps, setVirtualApps] = useState([]);
  const [tab, setTab] = useState("users");

  const load = async () => {
    const [u, r, v, vj, va] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/reports"),
      api.get("/admin/verifications"),
      api.get("/admin/virtual/jobs").catch(() => ({ data: [] })),
      api.get("/admin/virtual/applications").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data);
    setReports(r.data);
    setVerifications(v.data);
    setVirtualJobs(vj.data);
    setVirtualApps(va.data);
  };
  useEffect(() => { load(); }, []);

  const act = async (uid, action) => {
    try {
      await api.post(`/admin/users/${uid}/${action}`);
      toast.success(`User ${action}d`);
      load();
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const resolveReport = async (rid) => {
    await api.post(`/admin/reports/${rid}/resolve`);
    load();
  };

  const deleteVirtualJob = async (jid) => {
    if (!window.confirm("Delete this virtual job and all its applications?")) return;
    try {
      await api.delete(`/admin/virtual/jobs/${jid}`);
      toast.success("Virtual job deleted");
      load();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const decide = async (uid, decision, notes = "") => {
    try {
      await api.post(`/admin/verifications/${uid}/${decision}`, { notes });
      toast.success(`Verification ${decision}d`);
      load();
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const tabs = [
    { id: "users", label: `Users (${users.length})` },
    { id: "verifications", label: `Verifications (${verifications.length})` },
    { id: "reports", label: `Reports (${reports.length})` },
    { id: "virtual", label: `Virtual Jobs (${virtualJobs.length})` },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Admin</div>
            <h1 className="font-display text-4xl">Moderation</h1>
          </div>
          <Link to="/admin/blog" className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 font-semibold text-sm" data-testid="admin-blog-link">
            Manage blog →
          </Link>
        </div>

        <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm uppercase tracking-wider font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === t.id ? "border-brand text-brand" : "border-transparent text-slate-500"}`} data-testid={`admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold">{u.name}</td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3">{u.role || "—"}</td>
                    <td className="px-4 py-3">{u.status}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => act(u.user_id, "suspend")} className="text-xs uppercase tracking-wider font-semibold text-amber-600" data-testid={`suspend-${u.user_id}`}>Suspend</button>
                      <button onClick={() => act(u.user_id, "ban")} className="text-xs uppercase tracking-wider font-semibold text-accent-red" data-testid={`ban-${u.user_id}`}>Ban</button>
                      <button onClick={() => act(u.user_id, "activate")} className="text-xs uppercase tracking-wider font-semibold text-brand" data-testid={`activate-${u.user_id}`}>Activate</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "verifications" && (
          <div className="space-y-4">
            {verifications.length === 0 && <p className="text-slate-500 text-sm">No pending verifications.</p>}
            {verifications.map((v) => (
              <div key={v.user_id} className="border border-slate-200 p-5">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-display text-xl">{v.name}</div>
                      <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5">Pending</span>
                    </div>
                    <div className="text-xs text-slate-500 mb-3">{v.email} · {v.category} · {v.city}, {v.province}</div>
                    <dl className="text-sm space-y-1">
                      <div><dt className="inline font-semibold">Business: </dt><dd className="inline text-slate-700">{v.verification?.business_name}</dd></div>
                      <div><dt className="inline font-semibold">License #: </dt><dd className="inline text-slate-700 font-mono">{v.verification?.license_number}</dd></div>
                      {v.verification?.notes && <div><dt className="inline font-semibold">Notes: </dt><dd className="inline text-slate-700">{v.verification.notes}</dd></div>}
                    </dl>
                    <div className="flex gap-3 mt-3">
                      {v.verification?.license_image_url && (
                        <a href={v.verification.license_image_url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-brand underline" data-testid={`view-license-${v.user_id}`}>View license</a>
                      )}
                      {v.verification?.insurance_image_url && (
                        <a href={v.verification.insurance_image_url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-brand underline" data-testid={`view-insurance-${v.user_id}`}>View insurance</a>
                      )}
                    </div>
                  </div>
                  <div className="flex md:flex-col gap-2 md:w-44">
                    <button onClick={() => decide(v.user_id, "approve", "Approved")} className="flex-1 bg-emerald-600 text-white px-4 py-2 text-sm font-semibold flex items-center justify-center gap-1 hover:bg-emerald-700" data-testid={`approve-${v.user_id}`}>
                      <ShieldCheck size={14} weight="bold" /> Approve
                    </button>
                    <button onClick={() => {
                      const notes = window.prompt("Reason for rejection?");
                      if (notes !== null) decide(v.user_id, "reject", notes || "Insufficient documentation");
                    }} className="flex-1 bg-white border border-accent-red text-accent-red px-4 py-2 text-sm font-semibold flex items-center justify-center gap-1 hover:bg-red-50" data-testid={`reject-${v.user_id}`}>
                      <XCircle size={14} weight="bold" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "reports" && (
          <div className="space-y-3">
            {reports.length === 0 && <p className="text-slate-500 text-sm">No reports.</p>}
            {reports.map((r) => (
              <div key={r.report_id} className="border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold">{r.reason}</div>
                  <span className="text-[10px] uppercase tracking-wider bg-slate-100 px-2 py-0.5">{r.status}</span>
                </div>
                <p className="text-sm text-slate-700 mb-2">{r.description}</p>
                <div className="text-xs text-slate-500 mb-3">Target: <span className="font-mono">{r.target_user_id}</span> · Reporter: <span className="font-mono">{r.reporter_id}</span></div>
                {r.status === "open" && (
                  <button onClick={() => resolveReport(r.report_id)} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold" data-testid={`resolve-${r.report_id}`}>Resolve</button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "virtual" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Total jobs</div>
                <div className="text-2xl font-display mt-1">{virtualJobs.length}</div>
              </div>
              <div className="border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Open</div>
                <div className="text-2xl font-display mt-1">{virtualJobs.filter((j) => j.status === "open").length}</div>
              </div>
              <div className="border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Awarded</div>
                <div className="text-2xl font-display mt-1">{virtualJobs.filter((j) => j.status === "awarded").length}</div>
              </div>
              <div className="border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Applications</div>
                <div className="text-2xl font-display mt-1">{virtualApps.length}</div>
              </div>
            </div>
            <div className="border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Title</th>
                    <th className="text-left px-4 py-3">Client</th>
                    <th className="text-left px-4 py-3">Service</th>
                    <th className="text-left px-4 py-3">Budget</th>
                    <th className="text-left px-4 py-3">Apps</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {virtualJobs.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No virtual jobs yet.</td></tr>
                  ) : virtualJobs.map((j) => (
                    <tr key={j.job_id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold max-w-xs truncate"><Link to={`/virtual/jobs/${j.job_id}`} className="hover:text-brand">{j.title}</Link></td>
                      <td className="px-4 py-3 text-slate-600">{j.client_name}</td>
                      <td className="px-4 py-3 text-slate-600">{j.service_type}</td>
                      <td className="px-4 py-3 text-slate-600">${j.budget_min}–${j.budget_max}</td>
                      <td className="px-4 py-3 text-slate-600">{j.application_count || 0}</td>
                      <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-100">{j.status}</span></td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => deleteVirtualJob(j.job_id)} className="text-xs uppercase tracking-wider font-semibold text-accent-red" data-testid={`vdel-${j.job_id}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
